import { useQuery, useMutation } from '@tanstack/react-query';
import { apiCall } from '@/lib/api-client';
import type { NextClientResponse } from '@/types/api';
import { useActiveCallStore } from '@/stores/active-call.store';

export function useQueueRowDetails(assignmentId: string | null) {
  return useQuery({
    queryKey: ['queue', 'row-details', assignmentId],
    queryFn: () => apiCall<NextClientResponse>(`/agents/me/queue/${assignmentId}/details`),
    enabled: !!assignmentId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useClaimSpecific() {
  return useMutation({
    mutationFn: (assignmentId: string) =>
      apiCall<NextClientResponse>(`/agents/me/queue/${assignmentId}/claim`, { method: 'POST' }),
  });
}

import { useQueryClient } from '@tanstack/react-query';

export function useReleaseClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      apiCall<{ ok: boolean }>(`/agents/me/queue/${assignmentId}/release`, { method: 'POST' }),
    onSuccess: (_data, assignmentId) => {
      // Hotfix v0.3.0-v2 (Bug B race root cause):
      //
      // The v1 fix added clear() here, but the active-call store got
      // RE-POPULATED with the just-released assignment before the user's
      // navigate('/queue') completed. The culprit was inside PreCallPage:
      //
      //   const rehydrateQ = useQueueRowDetails(!data && assignmentId ? assignmentId : null);
      //   useEffect(() => {
      //     if (data) return;
      //     if (rehydrateQ.data) setCurrent(rehydrateQ.data);
      //   }, [data, rehydrateQ.data, setCurrent]);
      //
      // After clear() set data=null, the rehydrate query re-enabled. The
      // broad qc.invalidateQueries(['queue']) below was triggering a refresh
      // of ['queue', 'row-details', '<just-released-id>'], which got served
      // assignment data immediately (backend's getDetails returns rows for
      // any status, including 'pending' post-release). The effect then
      // re-applied setCurrent with the stale assignment data, undoing the
      // clear() before navigate had finished.
      //
      // Three changes here:
      //   1. clear() the store (was already in v1)
      //   2. REMOVE the specific row-details cache so the rehydrate effect
      //      sees no cached data on its next read.
      //   3. Drop the broad ['queue'] invalidation — only invalidate the
      //      queue LIST key. The broad one was re-fetching the row-details
      //      we just removed, defeating step 2.
      //
      // PreCallPage also gets a defensive guard (treat store as null when
      // its assignmentId doesn't match the URL) so this race can't bite
      // again if any caller of the rehydrate effect re-introduces it.
      useActiveCallStore.getState().clear();
      qc.removeQueries({ queryKey: ['queue', 'row-details', assignmentId] });
      // v0.3.2 fix: previous key ['agents', 'me', 'queue'] was a typo — no
      // useQuery in the codebase is registered with that key, so the invalidate
      // was a no-op. Real queue list key is QUEUE_KEY from useQueue.ts:5.
      void qc.invalidateQueries({ queryKey: ['queue', 'me'] });
      // Dashboard counts depend on assignment status too — invalidate for
      // symmetry with useTakeNext (useQueue.ts:32).
      void qc.invalidateQueries({ queryKey: ['agents', 'me', 'dashboard'] });
    },
  });
}
